// ResourceMeta / FieldMeta / ActionMeta — the shape of GET /v1/meta/resources[/:name]
// (blueprint §5.2). The engine renders every generic screen from this; nothing else
// knows a resource's fields. Until the backend serves it, meta/fallback.ts derives it.

import type { FilterOp } from '../api/client';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'number'
  | 'integer'
  | 'money'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'uuid'
  | 'relation'
  | 'json'
  | 'tags'
  | 'email'
  | 'phone'
  | 'url'
  | 'duration'
  | 'color';

export const FIELD_TYPES: FieldType[] = [
  'text', 'textarea', 'richtext', 'number', 'integer', 'money', 'boolean', 'date', 'datetime', 'enum', 'uuid',
  'relation', 'json', 'tags', 'email', 'phone', 'url', 'duration', 'color',
];

export interface EnumOption {
  value: string;
  label: string;
  /** Semantic tone for chips: pos | neg | warn | info | accent | muted. */
  tone?: string;
}

export interface RelationMeta {
  /** Target resource name (e.g. 'clients'). */
  resource: string;
  /** Field on the target shown as the label. */
  displayField: string;
  /** Field on the target used as the stored value. Defaults to the target's idField.
   *  (Deviation from §5.2: needed today because contracts/invoices store billing_party_id,
   *  which is a client's *billing_party_id*, not its id.) */
  valueField?: string;
  /** Extra filter applied when searching the target. */
  filter?: Record<string, unknown>;
}

/** The shape of a JSON field — mirrors src/lib/meta.ts JsonShape. Without one, json renders as a JSON editor. */
export type JsonShape =
  | { kind: 'object'; fields: ShapeField[] }
  | { kind: 'list'; item: ShapeField; addLabel?: string; max?: number }
  | { kind: 'map'; value?: ShapeField; keyLabel?: string; valueLabel?: string }
  | { kind: 'custom'; widget: string };

export interface ShapeField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  enum?: EnumOption[];
  help?: string;
  default?: unknown;
  shape?: JsonShape;
  relation?: RelationMeta;
}

export interface FieldMeta {
  name: string;
  label: string;
  type: FieldType;
  /** For json fields: how to render/edit the value. */
  shape?: JsonShape;
  required: boolean;
  readOnly: boolean;
  nullable: boolean;
  enum?: EnumOption[];
  relation?: RelationMeta;
  /** For money fields: sibling field carrying the ISO currency (defaults to 'currency'). */
  currencyField?: string;
  filterable: boolean;
  sortable: boolean;
  searchable: boolean;
  inCreate: boolean;
  inUpdate: boolean;
  inList: boolean;
  inDetail: boolean;
  /** Form section / column group. */
  group?: string;
  /** Sort order among fields. */
  order?: number;
  /** Widget override, resolved via ModuleUI.widgets then the field registry. */
  widget?: string;
  help?: string;
  /** Default value for create forms. */
  default?: unknown;
  /** Preferred filter ops (defaults derive from type). */
  ops?: FilterOp[];
  /** Display-only hint: right-align, tabular. */
  align?: 'left' | 'right' | 'center';
  /** Column width hint (px). */
  width?: number;
  /** Prefix applied when rendering as a link (e.g. '/clients/'). */
  linkTo?: string;
}

export interface ActionInput {
  type: 'object';
  properties: Record<string, { type: string; title?: string; description?: string; enum?: string[]; format?: string; default?: unknown }>;
  required?: string[];
}

export interface ActionMeta {
  name: string;
  label: string;
  method: 'POST' | 'GET' | 'PATCH' | 'PUT' | 'DELETE';
  /** Relative to the resource: ':id/issue', 'from-time', 'bulk'. */
  path: string;
  scope: 'collection' | 'item';
  /** JSON Schema for the request body (rendered as a small form when present). */
  input?: ActionInput;
  /** Confirmation copy; when set, the action asks first. */
  confirm?: string | { title: string; body: string; confirmLabel?: string; reason?: 'optional' | 'required' };
  tone?: 'default' | 'primary' | 'danger';
  permission?: string;
  icon?: string;
  /** Item actions: only offered when the row satisfies this (field → allowed values). */
  when?: Record<string, unknown[]>;
}

export type ViewKind = 'table' | 'board' | 'timeline' | 'calendar' | 'mailbox';

export interface ViewMeta {
  kind: ViewKind;
  /** Board: the enum/relation field that becomes columns. Timeline/calendar: start field. */
  field: string;
  /** Timeline/calendar: end field. */
  endField?: string;
  /** Card / bar label field (defaults to displayField). */
  labelField?: string;
}

export interface ResourceMeta {
  name: string;
  table: string;
  label: string;
  labelPlural: string;
  icon?: string;
  module: string;
  capabilities: { list: boolean; get: boolean; create: boolean; update: boolean; remove: boolean; bulk?: boolean };
  idField: string;
  displayField: string;
  /** e.g. '-created_at' */
  defaultSort: string;
  searchFields: string[];
  fields: FieldMeta[];
  actions: ActionMeta[];
  views?: ViewMeta[];
  /** Permission strings the caller holds for this resource (RBAC). */
  permissions: string[];
  /** Optional lifecycle: ordered enum values of `statusField` (rendered as the rail). */
  lifecycle?: { field: string; steps: string[]; terminal?: string[] };
}

export interface MetaIndex {
  resources: ResourceMeta[];
}

/** Sensible filter ops per field type. */
export function opsForType(t: FieldType): FilterOp[] {
  switch (t) {
    case 'text':
    case 'textarea':
    case 'richtext':
    case 'email':
    case 'phone':
    case 'url':
      return ['ilike', 'eq', 'ne', 'is_null'];
    case 'number':
    case 'integer':
    case 'money':
    case 'duration':
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null'];
    case 'date':
    case 'datetime':
      return ['eq', 'gte', 'lte', 'between', 'is_null'];
    case 'boolean':
      return ['eq'];
    case 'enum':
      return ['eq', 'ne', 'in', 'nin'];
    case 'uuid':
    case 'relation':
      return ['eq', 'ne', 'in', 'is_null'];
    case 'tags':
      return ['contains'];
    case 'json':
    case 'color':
      return ['eq'];
  }
}

export const OP_LABELS: Record<FilterOp, string> = {
  eq: 'is',
  ne: 'is not',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  in: 'is any of',
  nin: 'is none of',
  like: 'matches',
  ilike: 'contains',
  is_null: 'is empty',
  between: 'between',
  contains: 'includes',
};

/** Helper: find a field by name. */
export function fieldOf(meta: ResourceMeta, name: string): FieldMeta | undefined {
  return meta.fields.find((f) => f.name === name);
}

/** Fields sorted by `order` then declaration. */
export function orderedFields(meta: ResourceMeta): FieldMeta[] {
  return [...meta.fields].map((f, i) => ({ f, i })).sort((a, b) => (a.f.order ?? a.i) - (b.f.order ?? b.i) || a.i - b.i).map((x) => x.f);
}

/** Does the current principal hold `<resource>:<action>`? Missing permissions list = allow (pre-RBAC). */
export function can(meta: ResourceMeta | undefined, action: string): boolean {
  if (!meta) return false;
  if (!meta.permissions || meta.permissions.length === 0) return true;
  return meta.permissions.includes(`${meta.name}:${action}`) || meta.permissions.includes(`${meta.name}:*`) || meta.permissions.includes('*');
}
